/**
 * Module Color Utilities — Single Source of Truth
 *
 * Part of the Content domain (DDD B3).
 * Moved from src/utils/moduleColors.ts.
 *
 * Extracts the duplicated colorMap from HomePage, SubjectsPage, and ProgressPage
 * into a canonical utility. All module color lookups should use this.
 */

export interface ModuleColorSet {
  /** Solid background (e.g. bg-blue-500) */
  bg: string
  /** Gradient start (e.g. from-blue-500) */
  from: string
  /** Gradient end (e.g. to-cyan-500) */
  to: string
  /** Text color (e.g. text-blue-600) */
  text: string
  /** Border color (e.g. border-blue-200) */
  border: string
  /** Hex color for SVG strokes */
  ring: string
  /** Light background (e.g. bg-blue-50) */
  light: string
}

const MODULE_COLOR_MAP: Record<string, ModuleColorSet> = {
  'text-blue-500': {
    bg: 'bg-blue-500',
    from: 'from-blue-500',
    to: 'to-cyan-500',
    text: 'text-blue-600',
    border: 'border-blue-200',
    ring: '#3B82F6',
    light: 'bg-blue-50',
  },
  'text-indigo-500': {
    bg: 'bg-indigo-500',
    from: 'from-indigo-500',
    to: 'to-purple-500',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    ring: '#6366F1',
    light: 'bg-indigo-50',
  },
  'text-emerald-500': {
    bg: 'bg-emerald-500',
    from: 'from-emerald-500',
    to: 'to-teal-500',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    ring: '#10B981',
    light: 'bg-emerald-50',
  },
  'text-rose-500': {
    bg: 'bg-rose-500',
    from: 'from-rose-500',
    to: 'to-pink-500',
    text: 'text-rose-600',
    border: 'border-rose-200',
    ring: '#F43F5E',
    light: 'bg-rose-50',
  },
  'text-amber-500': {
    bg: 'bg-amber-500',
    from: 'from-amber-500',
    to: 'to-orange-500',
    text: 'text-amber-600',
    border: 'border-amber-200',
    ring: '#F59E0B',
    light: 'bg-amber-50',
  },
  'text-purple-500': {
    bg: 'bg-purple-500',
    from: 'from-purple-500',
    to: 'to-fuchsia-500',
    text: 'text-purple-600',
    border: 'border-purple-200',
    ring: '#8B5CF6',
    light: 'bg-purple-50',
  },
  'text-teal-500': {
    bg: 'bg-teal-500',
    from: 'from-teal-500',
    to: 'to-cyan-500',
    text: 'text-teal-600',
    border: 'border-teal-200',
    ring: '#14B8A6',
    light: 'bg-teal-50',
  },
  'text-cyan-500': {
    bg: 'bg-cyan-500',
    from: 'from-cyan-500',
    to: 'to-blue-500',
    text: 'text-cyan-600',
    border: 'border-cyan-200',
    ring: '#06B6D4',
    light: 'bg-cyan-50',
  },
  'text-orange-500': {
    bg: 'bg-orange-500',
    from: 'from-orange-500',
    to: 'to-amber-500',
    text: 'text-orange-600',
    border: 'border-orange-200',
    ring: '#F97316',
    light: 'bg-orange-50',
  },
  // Fase 0: TGL Split — TIJD module (Clock, sky blue)
  'text-sky-500': {
    bg: 'bg-sky-500',
    from: 'from-sky-500',
    to: 'to-blue-500',
    text: 'text-sky-600',
    border: 'border-sky-200',
    ring: '#0EA5E9',
    light: 'bg-sky-50',
  },
  // Fase 0: TGL Split — GELD module (Wallet, gold/yellow — avoids green/red validation conflict)
  'text-yellow-500': {
    bg: 'bg-yellow-500',
    from: 'from-yellow-500',
    to: 'to-amber-500',
    text: 'text-yellow-600',
    border: 'border-yellow-200',
    ring: '#EAB308',
    light: 'bg-yellow-50',
  },
  // SpellingWW module (PenTool, fuchsia — avoids red validation conflict)
  'text-fuchsia-500': {
    bg: 'bg-fuchsia-500',
    from: 'from-fuchsia-500',
    to: 'to-pink-500',
    text: 'text-fuchsia-600',
    border: 'border-fuchsia-200',
    ring: '#D946EF',
    light: 'bg-fuchsia-50',
  },
}

/** Default fallback color set (indigo) */
const DEFAULT_COLORS: ModuleColorSet = MODULE_COLOR_MAP['text-indigo-500']

/**
 * Get the full color set for a module based on its color class string.
 *
 * @param colorClass - The module's color property (e.g. 'text-blue-500')
 * @returns Full color set with bg, gradient, text, border, ring, and light variants
 */
export function getModuleColors(colorClass: string): ModuleColorSet {
  return MODULE_COLOR_MAP[colorClass] || DEFAULT_COLORS
}

/**
 * Get the hex ring color for SVG strokes.
 *
 * @param colorClass - The module's color property (e.g. 'text-blue-500')
 * @returns Hex color string (e.g. '#3B82F6')
 */
export function getModuleHexColor(colorClass: string): string {
  return (MODULE_COLOR_MAP[colorClass] || DEFAULT_COLORS).ring
}

export { MODULE_COLOR_MAP }
